import React from 'react';
import { motion } from 'framer-motion';
import { KeyRound, ShieldCheck, Zap, LifeBuoy } from 'lucide-react';

/**
 * قاب مشترک صفحه‌های ورود و ثبت‌نام مشتری.
 *
 * طراحی با مضمون «کلید لایسنس»: نیمه‌ی برند (فقط دسکتاپ) یک کارت لایسنسِ
 * شناور روی زمینه‌ی سبز تیره با الگوی نقطه‌ای دارد و نیمه‌ی فرم روشن و خلوت
 * است تا تمام توجه روی یک کار باشد. روی موبایل نیمه‌ی برند به یک نوار کوچک
 * بالای فرم خلاصه می‌شود — نمایش کارت بزرگ آنجا فقط فرم را پایین می‌راند.
 */

interface Props {
    eyebrow?: string;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
}

const PERKS = [
    { icon: Zap, text: 'خرید مستقیم و فعال‌سازی فوری لایسنس' },
    { icon: ShieldCheck, text: 'ورود بدون رمز، فقط با کد پیامکی' },
    { icon: LifeBuoy, text: 'پشتیبانی تیکتی با سابقه‌ی کامل' },
];

const AuthShell: React.FC<Props> = ({ eyebrow, title, subtitle, children, footer }) => (
    <div dir="rtl" className="min-h-screen bg-[#F7F5F0] lg:grid lg:grid-cols-[1.05fr_1fr]">
        {/* ----------------------------------------------------- نیمه‌ی برند */}
        <aside className="relative hidden overflow-hidden bg-[#06281F] text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
            {/* الگوی نقطه‌ای و هاله‌ها */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.18]"
                style={{
                    backgroundImage: 'radial-gradient(rgba(255,255,255,.55) 1px, transparent 1px)',
                    backgroundSize: '22px 22px',
                }}
            />
            <div aria-hidden className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-emerald-400/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-32 left-10 h-96 w-96 rounded-full bg-lime-300/10 blur-3xl" />

            <div className="relative flex items-center gap-3">
                <img src="/logo.png" alt="" className="h-10 w-10 rounded-xl bg-white/95 object-contain p-1" />
                <span className="text-lg font-bold tracking-tight">لایسنس اپ</span>
            </div>

            {/* کارت لایسنس */}
            <div className="relative mx-auto my-10 w-full max-w-sm [perspective:1200px]">
                <motion.div
                    initial={{ rotateX: 18, rotateY: -14, y: 20, opacity: 0 }}
                    animate={{ rotateX: 8, rotateY: -10, y: 0, opacity: 1 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="relative aspect-[1.6/1] overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-emerald-300/30 via-emerald-500/10 to-transparent p-6 shadow-[0_40px_80px_-20px_rgba(0,0,0,.6)] backdrop-blur-sm"
                >
                    {/* درخشش: نوار باریک مورب که از روی کارت عبور می‌کند. قبلاً یک لایه‌ی
                        تمام‌قد بود که بیرون از کارت می‌زد و لبه‌ی عمودی سخت داشت. */}
                    <motion.div
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/15 to-transparent"
                        initial={{ left: '-50%' }}
                        animate={{ left: ['-50%', '130%'] }}
                        transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
                    />
                    <div className="relative flex h-full flex-col justify-between">
                        <div className="flex items-center justify-between">
                            <span className="rounded-full bg-white/15 px-3 py-1 text-xs">لایسنس فعال</span>
                            <KeyRound className="h-6 w-6 text-emerald-200" />
                        </div>
                        <div>
                            <p dir="ltr" className="text-left font-mono text-lg tracking-[0.25em] text-white/90">
                                LX7Q-9K2M-H4TD
                            </p>
                            <div className="mt-3 flex items-center justify-between text-xs text-white/60">
                                <span>اعتبار تا ۱۴۰۶/۰۶/۳۱</span>
                                <span>۳ دستگاه</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            <div className="relative">
                <h2 className="text-3xl font-extrabold leading-[1.6]">
                    نرم‌افزارتان را
                    <span className="block text-emerald-300">در چند ثانیه فعال کنید.</span>
                </h2>
                <ul className="mt-8 space-y-3">
                    {PERKS.map(({ icon: Icon, text }, i) => (
                        <motion.li
                            key={text}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.35 + i * 0.1 }}
                            className="flex items-center gap-3 text-sm text-white/80"
                        >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                                <Icon className="h-4 w-4 text-emerald-300" />
                            </span>
                            {text}
                        </motion.li>
                    ))}
                </ul>
            </div>
        </aside>

        {/* ------------------------------------------------------ نیمه‌ی فرم */}
        <main className="flex min-h-screen flex-col">
            {/* سربرگ موبایل */}
            <div className="flex items-center gap-3 bg-[#06281F] px-5 py-4 text-white lg:hidden">
                <img src="/logo.png" alt="" className="h-9 w-9 rounded-lg bg-white/95 object-contain p-1" />
                <span className="font-bold">لایسنس اپ</span>
            </div>

            <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    className="w-full max-w-md"
                >
                    {eyebrow && (
                        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-emerald-700">{eyebrow}</p>
                    )}
                    <h1 className="text-[1.75rem] font-extrabold leading-tight text-[#0B1F19]">{title}</h1>
                    {subtitle && <p className="mt-2 text-sm leading-7 text-stone-600">{subtitle}</p>}

                    <div className="mt-8">{children}</div>

                    {footer && <div className="mt-8 border-t border-stone-200 pt-6 text-center text-sm text-stone-600">{footer}</div>}
                </motion.div>
            </div>
        </main>
    </div>
);

export default AuthShell;
